/* The four questions the planner cannot work without.
 *
 * Until now a new account was dropped straight onto a day built from defaults:
 * nine to six, asleep at eleven, a Sunday-to-Thursday week, no rituals and no
 * hobbies. Every one of those is a guess about a stranger's life, and the
 * first plan somebody sees — the one that decides whether they come back — was
 * therefore a plan for somebody else.
 *
 * Four steps, and the order is the order the day is built in:
 *
 *   1. **Sleep**, because it is the one window nothing may enter, and it
 *      bounds everything else.
 *   2. **Working hours and days**, because they decide where work may go —
 *      and, by their absence, where a hobby can.
 *   3. **Rituals**, the hours already spoken for every week. Offered as
 *      suggestions rather than a blank form: a blank form at step three is
 *      where people leave.
 *   4. **One hobby**, because a first plan with nothing but work in it teaches
 *      exactly the wrong lesson about what this is for.
 *
 * Every step is skippable and says what it costs to skip. An onboarding that
 * cannot be escaped is a wall in front of the product, and somebody who wants
 * to look around first is not doing anything wrong.
 */

import Icon from '../ui/Icon';
import type { IconName } from '../ui/Icon';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { post, put, patch, errorMessage } from '../api';
import { useSession } from '../session';
import { hhmm, toMinuteOfDay, human } from '../ui/time';

const WEEK = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' }
];

/* Offered, not written. Somebody who taps "The gym" has told us more in one
   press than a blank form gets out of most people in a minute. */
const RITUAL_IDEAS = [
  { title: 'The gym', startMin: 18 * 60, minutes: 60, days: [1, 3, 5] },
  { title: 'Lunch, properly', startMin: 13 * 60, minutes: 45, days: [0, 1, 2, 3, 4] },
  { title: 'The school run', startMin: 8 * 60, minutes: 45, days: [0, 1, 2, 3, 4] },
  { title: 'Friday night', startMin: 19 * 60, minutes: 180, days: [5] },
  { title: 'A walk', startMin: 17 * 60 + 30, minutes: 45, days: [1, 2, 3, 4, 5] }
];

const HOBBY_IDEAS = [
  { name: 'Gym', color: 'green', sessions: 3, each: 60 },
  { name: 'Reading', color: 'amber', sessions: 4, each: 45 },
  { name: 'Guitar', color: 'violet', sessions: 3, each: 45 },
  { name: 'Friends', color: 'blue', sessions: 2, each: 120 },
  { name: 'Running', color: 'orange', sessions: 3, each: 45 }
];

export default function OnboardingScreen({ onDone }: { onDone: () => void }) {
  const { user, refresh } = useSession();
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [sleepStart, setSleepStart] = useState(hhmm(user?.sleepStart ?? 1380));
  const [sleepEnd, setSleepEnd] = useState(hhmm(user?.sleepEnd ?? 420));
  const [workStart, setWorkStart] = useState(hhmm(user?.workStart ?? 540));
  const [workEnd, setWorkEnd] = useState(hhmm(user?.workEnd ?? 1080));
  const [workDays, setWorkDays] = useState<number[]>([0, 1, 2, 3, 4]);
  const [rituals, setRituals] = useState<string[]>([]);
  const [hobby, setHobby] = useState<string | null>(null);

  const finish = useMutation({
    mutationFn: async () => {
      /* Everything at the end rather than step by step: somebody who closes
         the tab at step two has not half-configured an account, and the
         planner never sees a state where sleep is saved and hours are not. */
      const sleep = await put('/api/sleep', {
        start: toMinuteOfDay(sleepStart), end: toMinuteOfDay(sleepEnd)
      });
      if (!sleep.ok) throw new Error(errorMessage(sleep, 'Could not save your sleep.'));

      const hours = await patch('/api/auth/me', {
        workStart: toMinuteOfDay(workStart), workEnd: toMinuteOfDay(workEnd)
      });
      if (!hours.ok) throw new Error(errorMessage(hours, 'Could not save your hours.'));

      await put('/api/preferences', { workDays });

      for (const title of rituals) {
        const idea = RITUAL_IDEAS.find(r => r.title === title);
        if (!idea) continue;
        await post('/api/rituals', {
          title: idea.title,
          startMin: idea.startMin,
          endMin: idea.startMin + idea.minutes,
          days: idea.days,
          protection: 'soft'
        });
      }

      if (hobby) {
        const idea = HOBBY_IDEAS.find(h => h.name === hobby);
        if (idea) await post('/api/hobbies', {
          name: idea.name, color: idea.color,
          sessionsPerWeek: idea.sessions,
          minutesPerWeek: idea.sessions * idea.each,
          kind: 'floor', category: 'must'
        });
      }

      const done = await post('/api/me/onboarded', {});
      if (!done.ok) throw new Error(errorMessage(done, 'Could not finish.'));
    },
    onSuccess: async () => { await refresh(); onDone(); },
    onError: (e: Error) => setError(e.message)
  });

  const skip = useMutation({
    mutationFn: async () => {
      const r = await post('/api/me/onboarded', {});
      if (!r.ok) throw new Error(errorMessage(r, 'Could not skip.'));
    },
    onSuccess: async () => { await refresh(); onDone(); },
    onError: (e: Error) => setError(e.message)
  });

  const toggle = <T,>(list: T[], v: T, set: (n: T[]) => void) =>
    set(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  const sleepLength = ((toMinuteOfDay(sleepEnd) - toMinuteOfDay(sleepStart)) + 1440) % 1440;
  const dayLength = toMinuteOfDay(workEnd) - toMinuteOfDay(workStart);

  const steps = [
    {
      icon: 'moon' as IconName,
      title: 'When do you sleep?',
      blurb: 'The one window nothing may be scheduled into — not by Amon, and not by you. ' +
             'Everything else is arranged around it, so it comes first.',
      body: (
        <>
          <div className="field">
            <label htmlFor="ob-sleep-start">Asleep by</label>
            <input id="ob-sleep-start" type="time" value={sleepStart}
                   onChange={e => setSleepStart(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="ob-sleep-end">Awake at</label>
            <input id="ob-sleep-end" type="time" value={sleepEnd}
                   onChange={e => setSleepEnd(e.target.value)} />
          </div>
          <p className="muted small field-wide">
            {human(sleepLength)} a night.
            {sleepLength < 6 * 60 && ' Amon will plan around it either way — it is your window.'}
          </p>
        </>
      )
    },
    {
      icon: 'briefcase' as IconName,
      title: 'And when do you work?',
      blurb: 'Work is only ever placed inside these hours. That is also what protects the ' +
             'rest of the day: an evening is free because work cannot reach it.',
      body: (
        <>
          <div className="field">
            <label htmlFor="ob-work-start">Start</label>
            <input id="ob-work-start" type="time" value={workStart}
                   onChange={e => setWorkStart(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="ob-work-end">Stop</label>
            <input id="ob-work-end" type="time" value={workEnd}
                   onChange={e => setWorkEnd(e.target.value)} />
          </div>
          <fieldset className="field-wide alarm-picker">
            <legend>Which days</legend>
            {WEEK.map(d => (
              <label key={d.value} className={`chip chip-pick${workDays.includes(d.value) ? ' is-on' : ''}`}>
                <input type="checkbox" checked={workDays.includes(d.value)}
                       onChange={() => toggle(workDays, d.value, setWorkDays)} />
                {d.label}
              </label>
            ))}
          </fieldset>
          <p className="muted small field-wide">
            {human(Math.max(0, dayLength))} a day, {workDays.length} days a week.
          </p>
        </>
      )
    },
    {
      icon: 'repeat' as IconName,
      title: 'Anything that happens every week?',
      blurb: 'The hours already spoken for: the gym, the school run, Friday night. Amon holds ' +
             'them before it offers your week to work — which is the difference between a ' +
             'thing you intend and a thing that happens.',
      body: (
        <fieldset className="field-wide alarm-picker">
          <legend>Tap any that are yours</legend>
          {RITUAL_IDEAS.map(r => (
            <label key={r.title} className={`chip chip-pick${rituals.includes(r.title) ? ' is-on' : ''}`}>
              <input type="checkbox" checked={rituals.includes(r.title)}
                     onChange={() => toggle(rituals, r.title, setRituals)} />
              {r.title}
            </label>
          ))}
          <p className="muted small field-wide">
            You can change the times, or add your own, in Settings afterwards.
          </p>
        </fieldset>
      )
    },
    {
      icon: 'hobbies' as IconName,
      title: 'And one thing that is not work?',
      blurb: 'A first plan with nothing but work in it teaches the wrong lesson about what ' +
             'this is for. Pick one and Amon will hold time for it every week.',
      body: (
        <fieldset className="field-wide alarm-picker">
          <legend>One is plenty to start with</legend>
          {HOBBY_IDEAS.map(h => (
            <label key={h.name} className={`chip chip-pick${hobby === h.name ? ' is-on' : ''}`}>
              <input type="radio" name="hobby" checked={hobby === h.name}
                     onChange={() => setHobby(h.name)} />
              {h.name}
            </label>
          ))}
          {hobby && (
            <p className="muted small field-wide">
              {(() => {
                const h = HOBBY_IDEAS.find(x => x.name === hobby)!;
                return `${h.sessions}× ${human(h.each)} a week, held back before work gets the time.`;
              })()}
            </p>
          )}
        </fieldset>
      )
    }
  ];

  const now = steps[step];
  const last = step === steps.length - 1;

  return (
    <div className="onboarding">
      <div className="onboarding-card">
        <div className="onboarding-head">
          <span className="muted small">Step {step + 1} of {steps.length}</span>
          <button className="linkish" onClick={() => skip.mutate()} disabled={skip.isPending}>
            Skip — I will set this up later
          </button>
        </div>

        <span className="onboarding-icon" aria-hidden="true"><Icon name={now.icon} size={26} /></span>
        <h1>{now.title}</h1>
        <p className="muted">{now.blurb}</p>

        <div className="when-form">{now.body}</div>

        {error && <p className="auth-err" role="alert">{error}</p>}

        <div className="inline">
          {step > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(s => s - 1)}><Icon name="left" size={15} />Back</button>
          )}
          {!last && (
            <button className="btn btn-primary btn-sm" onClick={() => setStep(s => s + 1)}>
              Next<Icon name="right" size={15} />
            </button>
          )}
          {last && (
            <button className="btn btn-primary btn-sm" onClick={() => finish.mutate()}
                    disabled={finish.isPending}>
              <Icon name="sparkle" size={15} />{finish.isPending ? 'Setting up…' : 'Build my first day'}
            </button>
          )}
        </div>

        <ol className="onboarding-dots" aria-hidden="true">
          {steps.map((_, i) => <li key={i} className={i <= step ? 'is-done' : ''} />)}
        </ol>
      </div>
    </div>
  );
}
